import type { StoryCategory } from "../types/models";

/**
 * Formats a StoryCategory enum value to a human-readable display name
 * @param category - The StoryCategory enum value
 * @returns Formatted display name
 */
export function formatCategoryName(category: StoryCategory | null | undefined): string {
  if (!category) return "All";

  // Handle camelCase categories by inserting spaces before capitals
  return category
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
}

/**
 * Gets the display title for a category section (e.g., "Top in Fiction")
 * @param category - The StoryCategory enum value
 * @returns Section title string
 */
export function getCategorySectionTitle(category: StoryCategory): string {
  const formatted = formatCategoryName(category);
  return `Top in ${formatted}`;
}

/**
 * Popular categories to feature on the homepage
 * Only categories with 10+ stories are included
 * Based on actual story distribution from migration report:
 * - FanFiction: 72 stories
 * - ScienceFiction: 61 stories
 * - Romance: 27 stories (2 + 25 from Relationships)
 * - PersonalGrowth: 36 stories (34 + 2 from Productivity)
 * - MentalHealth: 20 stories
 * - Lifestyle: 14 stories
 */
export const FEATURED_CATEGORIES: Array<{
  category: StoryCategory;
  title?: string; // Optional custom title
  priority: number; // Lower number = higher priority (shows first)
  minStories?: number; // Minimum number of stories required to show this section (default: 10)
}> = [
  // Fiction sub-categories (highest priority due to volume)
  { category: "FanFiction", title: "Top in Fan Fiction", priority: 1, minStories: 10 },
  { category: "ScienceFiction", title: "Top in Science Fiction", priority: 2, minStories: 10 },
  { category: "Romance", title: "Top in Romance", priority: 3, minStories: 10 },
  { category: "GeneralFiction", title: "Top in Fiction", priority: 4, minStories: 10 },
  
  // Non-fiction popular categories (10+ stories only)
  { category: "PersonalGrowth", title: "Top in Personal Growth", priority: 5, minStories: 10 },
  { category: "MentalHealth", title: "Top in Mental Health", priority: 6, minStories: 10 },
  { category: "Lifestyle", priority: 7, minStories: 10 },
];

/**
 * Gets the URL for exploring stories by category
 * @param category - The StoryCategory enum value
 * @returns URL path for the category filter (e.g., "/explore?category=Romance")
 */
export function getCategoryUrl(category: StoryCategory | null | undefined): string {
  if (!category) return "/explore";
  return `/explore?category=${encodeURIComponent(category)}`;
}

